// @ts-strict-ignore
import { CardsBlock, FieldBlock } from '@coast/schemas';
import { ReactElement, useContext, useMemo } from 'react';
import { View } from '@shared/components/View';
import { MBInlineCardList } from '@shared/components/blocks/lib/cards/MBInlineCardList';
import {
  joinRelatedCardsAndCardItems,
  useCardItems,
  useOpenLinkedCard,
} from '@shared/components/blocks/lib/cards/hooks';
import { MBFieldBlock } from '@shared/components/blocks/lib/field';
import { getTypoComponent } from '@shared/components/blocks/utils';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2 } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { MBCardQuantityPreview } from '../inputCards/quantity/preview';

export const MAX_CARDS_TO_SHOW = 6;

export interface LinkedCard {
  cardId: string;
  name?: string;
  quantity?: number;
}

interface Props {
  block: CardsBlock;
  onPress?(): void;
  renderLeft?(): ReactElement;
  relatedCards: API.CardFragment['relatedCards'];
}

export function MBCardsBlock({ block, onPress, renderLeft, relatedCards }: Props) {
  const colors = useContext(ColorThemeContext);
  const fieldBlock = useMemo(
    (): FieldBlock => ({ ...block, name: null, type: 'field', fieldType: 'text' }),
    [block],
  );

  const TypoComponent = getTypoComponent(block.textStyle);

  const cardItems = useCardItems(block.names);
  const cards = joinRelatedCardsAndCardItems(relatedCards, cardItems);

  const openLinkedCard = useOpenLinkedCard();

  if (block.mode === 'quantity' && !!cards.length) {
    return (
      <MBTouchableOpacity onPress={onPress}>
        <MBCardQuantityPreview label={block.label} cards={cards} onPressCard={openLinkedCard} />
      </MBTouchableOpacity>
    );
  }

  return (
    <MBFieldBlock
      value={null}
      block={fieldBlock}
      onPress={onPress}
      renderLeft={renderLeft}
      presenter={() => {
        if (cardItems.length === 0) {
          return (
            <TypoComponent color={colors.foregroundInactive}>{block.placeholder}</TypoComponent>
          );
        }

        return (
          <>
            <View row wrap shrinkWrap>
              <MBInlineCardList
                cards={cards?.slice(0, MAX_CARDS_TO_SHOW)}
                onPressCard={openLinkedCard}
                wrapCards
              />
            </View>
            {cards.length > MAX_CARDS_TO_SHOW && (
              <MBTextBody2>
                {i18n('card.blocks.cardsInput.overflow', {
                  count: cards.length - MAX_CARDS_TO_SHOW,
                })}
              </MBTextBody2>
            )}
          </>
        );
      }}
    />
  );
}
